import styled from '@emotion/styled'
import { useAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import {
  evalCondition,
  fromUTCtoZoneFormatted,
  notifyParent,
  STORE_KEY_LIST,
  storeGet,
} from 'mailup-react-helpers'
import {
  Actions,
  DataListRow,
  Flex,
  Grid,
  GRID_VARIANT_DEFAULT,
  GridCell,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TruncateAndTooltip,
} from 'mailup-web-components'
import { modalDeleteOpenCallback } from 'Pages/unifiedFiltersListPage/callbacks/modals'
import React, { useCallback, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { routesAccessConditions } from '../../../config/config'
import { pageState } from '../../../state/page'
import { FilterType, type FilterSummary } from '../../../types/beFiltersApi'
import { copyFilterCallback } from '../callbacks/filters'
import { COL_SPAN } from '../helpers/constants'
import { FilterTypeBadge } from './FilterTypeBadge'

interface FilterRowProps {
  filter: FilterSummary
}

const StyledLink = styled(Link)`
  word-break: break-all;
`

export const FilterRow: FC<FilterRowProps> = ({ filter }) => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)
  const intl = useIntl()
  const navigate = useNavigate()
  const [{ status }] = useAtom(pageState)
  const copyFilter = useAtomCallback(copyFilterCallback)
  const handleDelete = useAtomCallback(useCallback(modalDeleteOpenCallback, []))

  const labelEdit = intl.formatMessage({ id: 'Load.MailupCommon.Edit' })
  const labelCreateCopy = intl.formatMessage({
    id: 'Load.MailupCommon.CreateCopy',
  })
  const labelApplicaAEmail = intl.formatMessage({
    id: 'Load.Common.ApplicaAEmail',
  })
  const labelApplicaASms = intl.formatMessage({ id: 'Load.Common.ApplicaASms' })
  const labelDelete = intl.formatMessage({ id: 'Load.MailupCommon.Delete' })

  const deleteFilterPermission = evalCondition(
    routesAccessConditions.deleteFilter,
  ) as boolean
  const showEmailPermission = evalCondition(
    routesAccessConditions.showEmail,
  ) as boolean
  const showSmsPermission = evalCondition(
    routesAccessConditions.showSms,
  ) as boolean

  const editFilter = (filter: FilterSummary): void => {
    navigate(`/filters/form/${encodeURIComponent(filter.Id)}`)
  }
  
  const handleUseIn = (id: number, useIn: 'email' | 'sms'): void => {
    const r = `/recipients/list?sch=${useIn}&ssst=optin&saf=${encodeURIComponent(id)}`
    notifyParent('redirectMainFrame', [
      `recipients_app_redirector.aspx?r=${encodeURIComponent(r)}`,
    ])
  }
  const handleUseInEmailClick = (id: number): void => {
    handleUseIn(id, 'email')
  }
  const handleUseInSmsClick = (id: number): void => {
    handleUseIn(id, 'sms')
  }

  const actions = [
    {
      label: labelEdit,
      value: filter,
      onClick: editFilter,
    },

    {
      label: labelCreateCopy,
      onClick: copyFilter,
      value: filter.Id,
    },
    {
      label: labelApplicaAEmail,
      onClick: handleUseInEmailClick,
      value: filter.Id,
      hide: !showEmailPermission,
    },
    {
      label: labelApplicaASms,
      onClick: handleUseInSmsClick,
      value: filter.Id,
      hide: !showSmsPermission,
    },

    {
      label: labelDelete,
      onClick: handleDelete,
      value: filter.Id,
      hide: !deleteFilterPermission,
    },
  ]

  const displayCategory = (): string => {
    return filter.Category !== undefined
      ? `ID ${filter.Category.Id} - ${filter.Category.Name}`
      : '-'
  }

  return (
    <DataListRow key={filter.Id} data-mu-test={`filter-table-row_${filter.Id}`}>
      <Grid cols={12} variant={GRID_VARIANT_DEFAULT}>
        {status !== 'fetching' ? (
          <>
            <GridCell span={COL_SPAN.ID}>{filter.Id}</GridCell>
            <GridCell span={COL_SPAN.NAME}>
              <Flex flexDirection='column' gap='8px'>
                <StyledLink
                  onClick={() => {
                    editFilter(filter)
                  }}
                  variant={LINK_VARIANT_HIGHLIGHT}
                  data-tip={filter.Description}
                >
                  {filter.Name}
                </StyledLink>
                <TruncateAndTooltip text={filter.Notes ?? '-'} />
              </Flex>
            </GridCell>
            <GridCell span={COL_SPAN.TYPE}>
              <FilterTypeBadge fType={filter.Type} />
            </GridCell>
            <GridCell span={COL_SPAN.CATEGORY}>{displayCategory()}</GridCell>
            <GridCell span={COL_SPAN.LAST_UPDATE}>
              {fromUTCtoZoneFormatted(
                filter.UpdateDate,
                TimeZoneCode,
                'YYYY-MM-DD',
              )}
            </GridCell>
            <GridCell span={COL_SPAN.ACTIONS}>
              <Actions items={actions} />
            </GridCell>
          </>
        ) : (
          <>
            <GridCell span={COL_SPAN.ID}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.NAME}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.TYPE}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.CATEGORY}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.LAST_UPDATE}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.ACTIONS}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
          </>
        )}
      </Grid>
    </DataListRow>
  )
}
